import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { StatusTag } from '@/components/Tag/StatusTag';
import { FormField } from '@/components/Form/FormField';
import { PdfSectionHeading } from '../components/PdfSectionHeading';
import { PdfFormFieldValue } from '@/components/Pdf/components/PdfFormFieldValue';
import { PdfFormSection } from '@/components/Pdf/components/PdfFormSection';
import { PhotoWithMenu } from '@/components/Custom/Photo/PhotoWithMenu';
import { PdfPhotoListSection } from '../components/PdfPhotoSection';
import { PdfHeader } from '../components/PdfHeader';
import { useGetVehiculeCheckById } from '@/hooks/vehicule-check.hooks';
import { useGetResultByVehiculeCheckId } from '@/hooks/vehicule-check-result.hooks';
import Loader from '@/components/Loading/Loader';
import { getSortedList } from '@/hooks/list-order';
import { extractListPhotos } from '@/modules/VehiculeCheckResult/utils/result-image.utils';
import { TFunction } from 'i18next';
import { TResultObjectSchema } from '@/schemas/vehicule-check-result/result-object.schema';

interface ResultsPdfModelProps {
  t: TFunction<undefined>;
}

export const ResultsPdfModel = ({ t }: ResultsPdfModelProps) => {
  const { id } = useParams();

  const { data: vehiculeCheckResponse, isLoading: isLoadingVehiculeCheck } =
    useGetVehiculeCheckById(id);

  const {
    data: vehiculeCheckResultResponse,
    isLoading: isLoadingVehiculeCheckResult,
    refetch: refetchVehiculeCheckResult,
  } = useGetResultByVehiculeCheckId(id);

  useEffect(() => {
    refetchVehiculeCheckResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const vehiculeCheck = vehiculeCheckResponse?.data;
  const referenceNumber = vehiculeCheck?.referenceNumber;
  const clientLastname = vehiculeCheck?.individualLastname;
  const clientFirstname = vehiculeCheck?.individualFirstname;
  const clientEmail = vehiculeCheck?.individualEmail;
  const clientPhone = vehiculeCheck?.individualPhone;
  const clientAddressLine1 = vehiculeCheck?.individualAddressLine1;
  const clientAddressLine2 = vehiculeCheck?.individualAddressLine2;
  const clientPostalCode = vehiculeCheck?.individualPostalCode;
  const clientCity = vehiculeCheck?.individualCity;
  const clientCountry = vehiculeCheck?.individualCountry;

  const vehiculeCheckResult = vehiculeCheckResultResponse?.data;

  const vehicules = extractListPhotos(
    vehiculeCheckResultResponse?.data?.results,
    'vehicules'
  ) as TResultObjectSchema[];

  const equipments = extractListPhotos(
    vehiculeCheckResultResponse?.data?.results,
    'equipments'
  ) as TResultObjectSchema[];

  const damages = extractListPhotos(
    vehiculeCheckResultResponse?.data?.results,
    'damages'
  ) as TResultObjectSchema[];

  return (
    <div>
      <PdfHeader title={t('inspectionReport')} />

      {/* === INFORMATIONS === */}
      <div className="grid grid-cols-2 gap-x-5">
        <PdfFormSection
          heading={
            <PdfSectionHeading
              text={t('common:informations')}
              icon={<i className="ki-solid ki-information-2 text-lg" />}
              containerClassName="mb-3"
            />
          }
        >
          <>
            <div className="space-y-1">
              <FormField
                text={t('common:licensePlateNumber_short')}
                textLeftIcon={
                  <StatusTag
                    text="123"
                    className="self-start px-1.5 pt-[2.5px] pb-[3.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-2xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                  />
                }
                containerClassName="gap-0"
                textOuterWrapperClassName="w-[180px] items-center"
                textClassName="text-black text-[15px] font-medium"
              >
                <PdfFormFieldValue
                  text={vehiculeCheckResult?.valueImmatriculation || '--'}
                />
              </FormField>
              <FormField
                text={t('common:vin')}
                textLeftIcon={
                  <StatusTag
                    text="VIN"
                    className="self-start px-1.5 pt-[2.5px] pb-[3.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-2xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                  />
                }
                containerClassName="gap-0"
                textOuterWrapperClassName="w-[180px] items-center"
                textClassName="text-black text-[15px] font-medium"
              >
                <PdfFormFieldValue
                  text={vehiculeCheckResult?.valueVin || '--'}
                />
              </FormField>
            </div>
            <div>
              <FormField
                text={t('inspectionId')}
                containerClassName="gap-0"
                textOuterWrapperClassName="w-[180px] items-center"
                textClassName="text-black text-[15px] font-medium"
              >
                <PdfFormFieldValue text={id} />
              </FormField>
              <FormField
                text={t('common:referenceNumber_short_3')}
                containerClassName="gap-0"
                textOuterWrapperClassName="w-[180px] items-center"
                textClassName="text-black text-[15px] font-medium"
              >
                <PdfFormFieldValue text={referenceNumber || '--'} />
              </FormField>
            </div>
          </>
        </PdfFormSection>
        <PdfFormSection
          heading={
            <PdfSectionHeading
              text={t('common:client')}
              icon={<i className="ki-solid ki-user text-lg" />}
              containerClassName="mb-3"
            />
          }
        >
          <>
            <div>
              <FormField
                text={t('common:lastname')}
                containerClassName="gap-0"
                textOuterWrapperClassName="w-[180px] items-center"
                textClassName="text-black text-[15px] font-medium"
              >
                <PdfFormFieldValue text={clientLastname || '--'} />
              </FormField>
              <FormField
                text={t('common:firstname')}
                containerClassName="gap-0"
                textOuterWrapperClassName="w-[180px] items-center"
                textClassName="text-black text-[15px] font-medium"
              >
                <PdfFormFieldValue text={clientFirstname || '--'} />
              </FormField>
            </div>
            <div>
              <FormField
                text={t('common:email')}
                containerClassName="gap-0"
                textOuterWrapperClassName="w-[180px] items-center"
                textClassName="text-black text-[15px] font-medium"
              >
                <PdfFormFieldValue text={clientEmail || '--'} />
              </FormField>
              <FormField
                text={t('common:phoneNumber')}
                containerClassName="gap-0"
                textOuterWrapperClassName="w-[180px] items-center"
                textClassName="text-black text-[15px] font-medium"
              >
                <PdfFormFieldValue text={clientPhone || '--'} />
              </FormField>
            </div>
            <div>
              <FormField
                text={t('common:address')}
                containerClassName="gap-0"
                textOuterWrapperClassName="w-[180px] items-center"
                textClassName="text-black text-[15px] font-medium"
              >
                <div>
                  {clientAddressLine1 && (
                    <PdfFormFieldValue text={clientAddressLine1} />
                  )}
                  {clientAddressLine2 && (
                    <PdfFormFieldValue text={clientAddressLine2} />
                  )}
                  {clientPostalCode && (
                    <PdfFormFieldValue text={clientPostalCode} />
                  )}
                  {clientCity && <PdfFormFieldValue text={clientCity} />}
                  {clientCountry && <PdfFormFieldValue text={clientCountry} />}
                </div>
              </FormField>
            </div>
          </>
        </PdfFormSection>
      </div>
      {/* === END OF INFORMATIONS === */}

      {/* === VEHICULE LIST === */}
      <PdfPhotoListSection
        containerClassName="mt-5"
        heading={
          <PdfSectionHeading
            text={t('common:vehicules')}
            icon={<i className="ki-solid ki-picture text-lg" />}
            containerClassName="mb-3"
          />
        }
        isNotFound={!vehicules?.length}
        notFoundText={t('thisInspectionDoesHaveVehiculePhotos')}
      >
        {getSortedList(vehicules, 'updatedAt')?.map((item) => {
          const checkPoint = item?.['checkPoint'];

          return (
            <PhotoWithMenu
              key={checkPoint?.id}
              url={checkPoint?.imageCheckPoint}
              // TODO: Temporaily commenting out coordinates for checkpoint vehicle photos
              // detectionCoordinates={item?.images?.[0]?.['coordinatesModerated']}
              hideActionButtons
              showInfoWithinImage
              name={
                <div className="text-2sm text-center">{checkPoint?.name}</div>
              }
            />
          );
        })}
      </PdfPhotoListSection>
      {/* === END OF VEHICULE LIST === */}

      {/* === EQUIPMENT LIST === */}
      <PdfPhotoListSection
        containerClassName="mt-10"
        heading={
          <PdfSectionHeading
            text={t('common:equipments')}
            icon={<i className="ki-solid ki-picture text-lg" />}
            containerClassName="avoid-break mb-3"
          />
        }
        isNotFound={!equipments?.length}
        notFoundText={t('thisInspectionDoesHaveEquipmentPhotos')}
      >
        {getSortedList(equipments, 'updatedAt')?.map((item) => {
          return (
            <PhotoWithMenu
              key={item?.id}
              url={item?.images?.[0]?.url}
              detectionCoordinates={item?.images?.[0]?.['coordinatesModerated']}
              hideActionButtons
              showInfoWithinImage
              name={
                <div className="text-2sm text-center">
                  {item?.equipment?.name}
                </div>
              }
            />
          );
        })}
      </PdfPhotoListSection>
      {/* === END OF EQUIPMENT LIST === */}

      {/* === DAMAGE LIST === */}
      <PdfPhotoListSection
        containerClassName="mt-10"
        heading={
          <PdfSectionHeading
            text={t('common:damages')}
            icon={<i className="ki-solid ki-picture text-lg" />}
            containerClassName="avoid-break mb-3"
          />
        }
        isNotFound={!damages?.length}
        notFoundText={t('thisInspectionDoesHaveDamagePhotos')}
      >
        {getSortedList(damages, 'updatedAt')?.map((item) => {
          return (
            <PhotoWithMenu
              key={item?.id}
              url={item?.images?.[0]?.url}
              detectionCoordinates={item?.images?.[0]?.['coordinatesModerated']}
              originalBoundingBox={item?.['original_bbox']}
              hideActionButtons
              showInfoWithinImage
              name={
                <div className="px-1.5">
                  <div>
                    <span className="text-2sm font-medium">
                      {t('common:equipment')} :
                    </span>
                    &nbsp;
                    <span className="text-2sm">{item?.equipment?.name}</span>
                  </div>
                  <div>
                    <span className="text-2sm font-medium">
                      {t('common:damage')} :
                    </span>
                    &nbsp;
                    <span className="text-2sm">
                      {item?.damage?.name?.split('_')?.[0]}
                    </span>
                  </div>
                  <div>
                    <span className="text-2sm font-medium">
                      {t('common:severity')} :
                    </span>
                    &nbsp;
                    <span className="text-2sm">
                      {item?.damage?.name?.split('_')?.pop()}
                    </span>
                  </div>
                </div>
              }
            />
          );
        })}
      </PdfPhotoListSection>
      {/* === END OF DAMAGE LIST === */}

      {/* === REPAIR LIST === */}
      {/* <PdfPhotoListSection
        containerClassName="mt-10"
        heading={
          <PdfSectionHeading
            text="Réparations"
            icon={<i className="ki-solid ki-picture text-lg" />}
            containerClassName="avoid-break mb-3"
          />
        }
        isNotFound={true}
        notFoundText={t('repairsNotFound')}
      >
        <></>
      </PdfPhotoListSection> */}
      {/* === END OF REPAIR LIST === */}

      {(isLoadingVehiculeCheck || isLoadingVehiculeCheckResult) && <Loader />}
    </div>
  );
};
